// Semi-monthly pay period helpers. Pay periods run 1st-15th and 16th-end of month.
// Shared by the payroll expense view and the TimeCamp admin report.

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export const toStartOfDay = (value) => {
  if (!value) return null;
  let d = null;
  if (value instanceof Date) {
    d = new Date(value);
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = DATE_ONLY_PATTERN.exec(trimmed);
    if (match) {
      const year = Number(match[1]);
      const monthIndex = Number(match[2]) - 1;
      const day = Number(match[3]);
      d = new Date(year, monthIndex, day);
    } else {
      d = new Date(value);
    }
  } else {
    d = new Date(value);
  }
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

export const toDateOnlyString = (value) => {
  const d = toStartOfDay(value);
  if (!d) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Collapsed form for a picker that has to sit in a crowded filter row. The
// periods are fixed halves of a month, so the start date alone identifies one —
// the full range is still what the open menu lists.
export const formatPeriodStartLabel = (start) => {
  const date = toStartOfDay(start);
  if (!date) return '';
  return `Starting ${date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })}`;
};

export const formatPeriodLabel = (start, end) => {
  const s = start.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  const e = end.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  return `${s} - ${e}`;
};

export const getHalfMonthPeriodsForMonth = (year, monthIndex) => {
  const firstStart = new Date(year, monthIndex, 1);
  const firstEnd = new Date(year, monthIndex, 15);
  const secondStart = new Date(year, monthIndex, 16);
  const secondEnd = new Date(year, monthIndex + 1, 0);
  return [
    { start: firstStart, end: firstEnd },
    { start: secondStart, end: secondEnd },
  ];
};

export const getCurrentPayPeriod = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  if (now.getDate() <= 15) {
    return {
      start: new Date(year, month, 1),
      end: new Date(year, month, 15),
    };
  }

  return {
    start: new Date(year, month, 16),
    end: new Date(year, month + 1, 0),
  };
};

// Returns selectable pay periods spanning `monthsBack` to `monthsForward` months,
// newest first.
export const getPayPeriodOptions = ({ monthsBack = 6, monthsForward = 6 } = {}) => {
  const options = [];
  const now = new Date();
  const baseYear = now.getFullYear();
  const baseMonth = now.getMonth();

  for (let offset = -monthsBack; offset <= monthsForward; offset += 1) {
    const monthDate = new Date(baseYear, baseMonth + offset, 1);
    const periods = getHalfMonthPeriodsForMonth(monthDate.getFullYear(), monthDate.getMonth());
    periods.forEach((period) => {
      options.push({
        value: `${toDateOnlyString(period.start)}|${toDateOnlyString(period.end)}`,
        label: formatPeriodLabel(period.start, period.end),
        start: period.start,
        end: period.end,
      });
    });
  }

  return options.sort((a, b) => b.start.getTime() - a.start.getTime());
};

// A full day at the standard schedule, before any hours reduction is applied.
export const STANDARD_WORK_DAY_HOURS = 8;

// Mon-Fri days in [start, end], both ends inclusive. Statutory holidays and
// company close dates are NOT excluded: this is the raw capacity of the span,
// which is what TAWH is built on (see utils/tawh.js). Time actually taken for a
// closure is credited separately, so taking it out here as well would charge
// the employee for the holiday twice.
export const listWorkingDays = (start, end) => {
  const from = toStartOfDay(start);
  const to = toStartOfDay(end);
  if (!from || !to || from > to) return [];

  const days = [];
  const cursor = new Date(from);
  while (cursor <= to) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) days.push(toDateOnlyString(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

export const countWorkingDays = (start, end) => listWorkingDays(start, end).length;

// Short label for a period, matching how the business names them: "Jan 1-15",
// "Feb 16-28".
export const formatPeriodShortLabel = (startIso, endIso) => {
  const from = toStartOfDay(startIso);
  const to = toStartOfDay(endIso);
  if (!from || !to) return '';
  return `${from.toLocaleDateString('en-US', { month: 'short' })} ${from.getDate()}-${to.getDate()}`;
};

// Every whole semi-monthly pay period contained in [startIso, endIso], oldest
// first. Generated rather than looked up, so it does not run out at the end of
// a year the way the old hours table did.
//
// Containment is strict: a period only counts if the range covers all of it. A
// half-covered period has no meaningful target, and silently counting it whole
// would overstate the hours to work.
export const listPayPeriodsInRange = (startIso, endIso) => {
  const from = toStartOfDay(startIso);
  const to = toStartOfDay(endIso);
  if (!from || !to || from > to) return [];

  const periods = [];
  // Walk months from the one the range opens in to the one it closes in, and
  // keep the halves that fall entirely inside it.
  const cursor = new Date(from.getFullYear(), from.getMonth(), 1);
  const last = new Date(to.getFullYear(), to.getMonth(), 1);
  while (cursor <= last) {
    getHalfMonthPeriodsForMonth(cursor.getFullYear(), cursor.getMonth()).forEach((period) => {
      if (period.start < from || period.end > to) return;
      const startDate = toDateOnlyString(period.start);
      const endDate = toDateOnlyString(period.end);
      periods.push({
        id: startDate,
        title: formatPeriodShortLabel(startDate, endDate),
        startDate,
        endDate,
      });
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return periods;
};
